import Router, { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { cache } from 'swr';
import { routes } from '../../../config/routes';
import { SessionStorageKeys } from '../../../constants/storage';
import { useAppContext } from '../../../store';
import { pxToRem } from '../../../styles/utils';
import { clearStorage, getStorage, setStorage } from '../../../utils/storage';
import { footerHeightMobile, footerHeightWeb } from '../Footer';
import { useHeaderHeight } from '../Header';
import { Modal, P1 } from 'pfm-ui-components';
import { useState } from 'react';
import {
  BACK_BUTTON_MODAL_MESSAGE,
  TOKEN,
  LAST_LOGIN,
  CHANNEL_ID,
  CUSTOMER_ID,
  CHANNEL_ID_TYPES,
  CUST_ID,
} from '../../../constants/common';
import API from '../../../API';
import { getCookiesOptions } from '../../../utils/common';
import { unsetAuthToken } from '../../../API/utils';

interface AppComponentWrapperProps {
  children: JSX.Element | JSX.Element[];
  hasFooter: boolean;
  hasHeader: boolean;
  showHeader: boolean;
}

export default function AppComponentWrapper(props: AppComponentWrapperProps) {
  const { children, hasFooter, hasHeader, showHeader } = props;
  const [cookie, setCookie, removeCookie] = useCookies([TOKEN]);
  const { state, setState, resetState } = useAppContext();
  const [backButtonModal, setBackButtonModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessageShownInModal, setErrorMessageShownInModal] = useState('');

  const logoutAndRedirectToLogin = () => {
    API.login
      .logout()
      .then((res) => {
        clearStorage();
        removeCookie(TOKEN, getCookiesOptions());
        removeCookie(CUSTOMER_ID, getCookiesOptions());
        removeCookie(LAST_LOGIN, getCookiesOptions());
        removeCookie(CHANNEL_ID, getCookiesOptions());
        removeCookie(CUST_ID,getCookiesOptions());
        unsetAuthToken();
        // Router.push(routes.LOGIN.url);
        switch(state.channelId){
          case CHANNEL_ID_TYPES.MBK:
            router.push(routes.SESSION_EXPIRED.url);  
            break;
          default:
            window.location.assign(process.env.MF_OMNI_ONELINK);
        }
        resetState();
        cache.clear();
      })
      .catch((err) => {
        setOpenErrorModal(true);
        setErrorMessageShownInModal(err?.errorMsg?.message);
      });
  };

  function browserBackHandler() {
    if (state && state.inAppBackPressed) {
      setState({ ...state, inAppBackPressed: false });
      return true;
    }
    setBackButtonModal(true);
    return false;
  }

  useEffect(() => {
    Router.beforePopState(browserBackHandler);
  }, [state]);

  useEffect(() => {
    let applicationAlreadyLoaded = getStorage(SessionStorageKeys.APPLICATION_LOADED);
      // Below code is to stop application from allowing page reloads/back/forward button clicks on the browser window
      const entries = performance.getEntriesByType("navigation");
      entries.forEach((entry: any) => {
        if ((entry.type === "reload" || entry.type === "back_forward" ) && applicationAlreadyLoaded && cookie[TOKEN]) {
          logoutAndRedirectToLogin();
        }
      });
    Router.beforePopState(browserBackHandler);
    // To scroll to top on every page load
    Router.events.on('routeChangeComplete', () => {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    });
    // if (window !== undefined) {
    //   window.onbeforeunload = function () {
    //     return 'Are you sure you want to leave?';
    //   };
    // }

    setStorage(SessionStorageKeys.APPLICATION_LOADED, true);
    // Do not show the header if login is via MBK channel
    const showHeader = cookie[CHANNEL_ID] !== CHANNEL_ID_TYPES.MBK;
    setState({ ...state, showHeader: showHeader });
  }, []);

  useEffect(() => {
    setState({ ...state, showHeader: showHeader });
  }, [showHeader]);

  const router = useRouter();
  useEffect(() => {
    if (state && state.inAppBackPressed) {
      router.back();
    }
  }, [state, state.inAppBackPressed]);

  const headerHeight = useHeaderHeight();
  return (
    <>
      {openErrorModal && (
        <Modal onClose={() => setOpenErrorModal(false)} mode="alert" showHeader={false}>
          <StyledP1>{errorMessageShownInModal}</StyledP1>
        </Modal>
      )}
      {backButtonModal && (
        <StyledModal
          mode="alert"
          className="back-button-modal"
          wrapperClassname="back-button-modal-wrapper"
          contentClassname="back-button-modal-content"
          showHeader={false}
          onClose={() => {
            setBackButtonModal(false);
            logoutAndRedirectToLogin();
            Router.beforePopState(() => false);
          }}
        >
          {BACK_BUTTON_MODAL_MESSAGE}
        </StyledModal>
      )}
      <ComponentWrapper hasFooter={hasFooter} hasHeader={hasHeader} headerHeight={headerHeight}>
        {children}
      </ComponentWrapper>
    </>
  );
}

const ComponentWrapper = styled.div<{ hasFooter: boolean; hasHeader: boolean; headerHeight: string }>`
  width: 100%;
  min-width: 360px;
  min-height: 100vh;
  padding-top: ${(props) => (props.hasHeader ? props.headerHeight : 0)};
  padding-bottom: ${(props) => (props.hasFooter ? pxToRem(footerHeightMobile + 24) : 0)};
  ${up('md')} {
    padding-bottom: ${(props) => (props.hasFooter ? pxToRem(footerHeightWeb + 24) : 0)};
  }
`;

const StyledModal = styled(Modal)`
  .back-button-modal-wrapper {
    display: flex;
  }

  .back-button-modal-content {
    align-self: center;
    margin: 0 auto;
    left: 0;
  }
`;

const StyledP1 = styled(P1)`
  color: ${(props) => props.theme.palette.grey2};
`;
